import {
  Autocomplete,
  createFilterOptions,
  debounce,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import WorkLocationAutocompleteField from "../../../common/components/functional/fields/workLocationAutocompleteField/WorkLocationAutocompleteField";
import WorkTitleAutocompleteField from "../../../common/components/functional/fields/WorkTitleAutocompleteField";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { customerOrgAndCompAutocomplete } from "../../customer/customerAPI";

export type ExclusivityFormValues = {
  customerCompany: { id: string; name: string } | null;
  customerOrganization: { id: string; name: string } | null;
  workLocation: { id: string; name: string } | null;
  workTitles: { id: string; name: string }[];
};

type ExclusivityOption = { id: string; name: string } | null;

type Props = {
  isEditing?: boolean;
};

export const AddExclusivityForm = ({ isEditing = false }: Props) => {
  const { t } = useTranslation();

  const [customerCompanySearchValue, setCustomerCompanySearchValue] =
    useState("");
  const [customerCompanies, setCustomerCompanies] = useState<
    CustomerOrgCompany[]
  >([]);

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ExclusivityFormValues>();

  const { customerCompany, customerOrganization, workTitles } = watch();

  useEffect(() => {
    if (customerCompanySearchValue.length >= 2) {
      customerOrgAndCompAutocomplete({ name: customerCompanySearchValue }).then(
        (res) => setCustomerCompanies(res),
      );
    }
  }, [customerCompanySearchValue]);

  const handleCustomerCompanySearchChange = debounce(
    (value: string) => setCustomerCompanySearchValue(value),
    400,
  );

  const customerCompanyOptions = useMemo(() => {
    if (!customerCompanies.length) return [];
    return customerCompanies.map((item) => ({ id: item.id, name: item.name }));
  }, [customerCompanies]);

  const customerOrgOptions = useMemo(() => {
    if (!customerCompany || !customerCompanies.length) return [];
    return (
      customerCompanies
        .find((company) => company.id === customerCompany.id)
        ?.customerOrganizations?.map((org) => ({
          id: org.id,
          name: org.name,
        })) ?? []
    );
  }, [customerCompany, customerCompanies]);

  const handleCustomerCompanyChange = (
    currentValue: ExclusivityOption,
    newValue: ExclusivityOption,
    onChange: (value: ExclusivityOption) => void,
  ) => {
    if (currentValue?.id !== newValue?.id) {
      setValue("customerOrganization", null);
      setValue("workLocation", null);
    }
    onChange(newValue);
  };

  const handleCustomerOrganizationChange = (
    currentValue: ExclusivityOption,
    newValue: ExclusivityOption,
    onChange: (value: ExclusivityOption) => void,
  ) => {
    if (currentValue?.id !== newValue?.id) {
      setValue("workLocation", null);
    }
    onChange(newValue);
  };

  return (
    <Grid container rowSpacing={1} columnSpacing={2}>
      <FormGridRow label={`${t("talentProfile:customerCompany")} *`}>
        <Controller
          control={control}
          name="customerCompany"
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              value={field.value}
              disabled={isEditing}
              options={customerCompanyOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              filterOptions={createFilterOptions({
                stringify: (option) => JSON.stringify(option),
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("talentProfile:selectCustomerCompany")}
                  size="small"
                  error={!!errors.customerCompany}
                />
              )}
              onChange={(_, newValue) =>
                handleCustomerCompanyChange(
                  field.value,
                  newValue,
                  field.onChange,
                )
              }
              onInputChange={(_, newValue) => {
                if (newValue) {
                  handleCustomerCompanySearchChange(newValue);
                }
              }}
            />
          )}
        />
      </FormGridRow>

      <FormGridRow
        label={t("talentProfile:organization")}
        tooltip={
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:exclusivityCustomerOrgInfo")}
          />
        }
      >
        <Controller
          control={control}
          name="customerOrganization"
          render={({ field }) => (
            <Autocomplete
              value={field.value}
              options={customerOrgOptions}
              disabled={!customerCompany}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("talentProfile:selectOrganization")}
                  size="small"
                  error={!!errors.customerOrganization}
                />
              )}
              onChange={(_, newValue) =>
                handleCustomerOrganizationChange(
                  field.value,
                  newValue,
                  field.onChange,
                )
              }
            />
          )}
        />
      </FormGridRow>

      <FormGridRow
        label={t("workLocation")}
        tooltip={
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:exclusivityWorkLocationInfo")}
          />
        }
      >
        <WorkLocationAutocompleteField
          name="workLocation"
          fullWidth
          customerOrgId={customerOrganization?.id}
          disabled={!customerOrganization}
          groupByCustomerOrg={false}
          useFixedOptions
          placeholder={t("talentProfile:selectWorkLocation")}
        />
      </FormGridRow>

      <FormGridRow
        label={t("workTitles")}
        multiRow
        tooltip={
          <InfoTooltip
            style={{ marginLeft: "8px" }}
            title={t("talentProfile:exclusivityWorkTitleInfo")}
          />
        }
      >
        <WorkTitleAutocompleteField
          name="workTitles"
          fullWidth
          fetchAll
          groupByFieldOfWork
          disabled={!customerCompany}
          placeholder={
            !workTitles?.length ? t("talentProfile:selectWorkTitles") : undefined
          }
        />
      </FormGridRow>
    </Grid>
  );
};
